/**
 * Clinical notes contracts (BUILD-PLAN M10, research/library/chart-notes.md 4).
 * Owner: WP2-NOTES.
 *
 * A note is structured JSON (`NoteBody`: free-text blocks and template
 * blocks with typed field answers); `renderedText` is a rendering of it,
 * never the other way round. Templates are inline forms: every field is
 * visible at once, required fields block signing, optional fields that are
 * left blank drop their whole clause, and branch rules hide follow-on fields.
 *
 * Lifecycle: draft -> signed (locks the text) | pending_cosign (the
 * additional provider signed; the primary still has to) | locked_unsigned
 * (auto-locked at the office's local midnight; the text is frozen and a later
 * signature carries a "late signature" stamp). Corrections after a lock are
 * addenda or strike-through amendments, never edits.
 *
 * Write-back: a signed note at a PMS office is queued as a `note.add`
 * command (Dentrix `add_clinical_note`, stable on PulseSync v1). Open Dental
 * and Eaglesoft are blocked on v1, so their notes stay in Velora with the
 * reason shown.
 */
import { z } from 'zod';
import { IdSchema, InstantSchema, LocalDateSchema, WriteBackStateSchema } from './sync.js';

/* ------------------------------------------------------------------------ */
/* Templates                                                                 */
/* ------------------------------------------------------------------------ */

export const TEMPLATE_FIELD_TYPES = [
  'single',
  'multi',
  'text',
  'number',
  'tooth',
  'surface',
  'date',
] as const;
export const TemplateFieldTypeSchema = z.enum(TEMPLATE_FIELD_TYPES);
export type TemplateFieldType = z.infer<typeof TemplateFieldTypeSchema>;

/** Where a field's default comes from (shown muted until confirmed). */
export const DEFAULT_SOURCES = [
  'appointment.provider',
  'appointment.additional_provider',
  'appointment.date',
  'note.teeth',
  'procedures.today',
  'patient.alerts',
  /** Premedication alerts only (the premed note's Indication; assistant role test fix 3). */
  'patient.premed_alerts',
] as const;
export const DefaultSourceSchema = z.enum(DEFAULT_SOURCES);
export type DefaultSource = z.infer<typeof DefaultSourceSchema>;

export const FieldKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/);

export const TemplateFieldSchema = z
  .object({
    key: FieldKeySchema,
    label: z.string().min(1).max(80),
    type: TemplateFieldTypeSchema,
    required: z.boolean().default(false),
    /** single and multi only. */
    options: z.array(z.string().min(1).max(80)).max(40).default([]),
    /** Branch rules: choosing this option hides these fields (OD nested Auto Notes). */
    hides: z.record(z.string(), z.array(FieldKeySchema)).default({}),
    /** A literal default, muted until confirmed. */
    defaultValue: z
      .union([z.string(), z.array(z.string())])
      .nullable()
      .default(null),
    defaultFrom: DefaultSourceSchema.nullable().default(null),
    unit: z.string().max(16).nullable().default(null),
    /** Optional S/O/A/P tag, so the AI scribe can fill the same template. */
    section: z.enum(['S', 'O', 'A', 'P']).nullable().default(null),
  })
  .strict();
export type TemplateField = z.infer<typeof TemplateFieldSchema>;
export type TemplateFieldInput = z.input<typeof TemplateFieldSchema>;

/**
 * Template text. `{{key}}` places a field; `[[ ... ]]` marks an optional
 * clause that is dropped when any field inside it is blank or hidden; a line
 * holding a blank field outside a clause is dropped whole; `## Title` starts
 * a heading that survives only when a line under it does.
 */
export const TemplateBodySchema = z
  .object({
    source: z.string().min(1).max(20_000),
    fields: z.array(TemplateFieldSchema).max(60),
  })
  .strict();
export type TemplateBody = z.infer<typeof TemplateBodySchema>;
export type TemplateBodyInput = z.input<typeof TemplateBodySchema>;

export const TEMPLATE_CATEGORIES = [
  'Exams',
  'Hygiene',
  'Periodontics',
  'Anesthesia',
  'Restorative',
  'Fixed prosthetics',
  'Removable prosthetics',
  'Endodontics',
  'Oral surgery',
  'Radiographs',
  'Prescriptions',
  'Consent',
  'Post-op',
  'Emergency',
  'Pediatric',
  'Administrative',
] as const;
export const TemplateCategorySchema = z.enum(TEMPLATE_CATEGORIES);
export type TemplateCategory = z.infer<typeof TemplateCategorySchema>;

export const TemplateScopeSchema = z.enum(['starter', 'org', 'location', 'user']);
export type TemplateScope = z.infer<typeof TemplateScopeSchema>;

export const TemplateStatusSchema = z.enum(['draft', 'published', 'retired']);
export type TemplateStatus = z.infer<typeof TemplateStatusSchema>;

/** Slash aliases are short words: `/anes`, `/perio`, `/rx`. */
export const SlashAliasSchema = z.string().regex(/^[a-z][a-z0-9-]{1,23}$/);

export const NoteTemplateSchema = z.object({
  /** Row id of this version (starter templates use `starter:<key>`). */
  id: z.string().min(1),
  /** Stable family key shared by every version. */
  key: z.string().min(1),
  version: z.number().int().positive(),
  name: z.string().min(1).max(120),
  category: TemplateCategorySchema,
  scope: TemplateScopeSchema,
  status: TemplateStatusSchema,
  locationId: IdSchema.nullable(),
  ownerUserId: IdSchema.nullable(),
  aliases: z.array(SlashAliasSchema),
  /** CDT codes whose completion pre-drafts this template (draft only). */
  defaultForCodes: z.array(z.string().regex(/^D\d{4}$/)),
  body: TemplateBodySchema,
  publishedAt: InstantSchema.nullable(),
  publishedByName: z.string().nullable(),
  changeNote: z.string().nullable(),
  favorite: z.boolean(),
  /** The caller may edit this family in place (new draft version). */
  canEdit: z.boolean(),
  /** A newer draft of this family exists. */
  hasDraft: z.boolean(),
});
export type NoteTemplate = z.infer<typeof NoteTemplateSchema>;

export const TemplateListSchema = z.object({
  templates: z.array(NoteTemplateSchema),
  /** The caller can publish org templates. */
  canPublish: z.boolean(),
});
export type TemplateList = z.infer<typeof TemplateListSchema>;

export const SaveTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  category: TemplateCategorySchema,
  aliases: z.array(SlashAliasSchema).max(8).default([]),
  defaultForCodes: z
    .array(z.string().regex(/^D\d{4}$/))
    .max(20)
    .default([]),
  body: TemplateBodySchema,
  changeNote: z.string().max(500).optional(),
});
export type SaveTemplateRequest = z.input<typeof SaveTemplateRequestSchema>;

/** Create a personal template, or a personal copy of any template. */
export const CreateTemplateRequestSchema = SaveTemplateRequestSchema.extend({
  scope: z.enum(['user', 'org']).default('user'),
});
export type CreateTemplateRequest = z.input<typeof CreateTemplateRequestSchema>;

export const TemplateVersionsSchema = z.object({
  versions: z.array(NoteTemplateSchema),
});

/** Open Dental Auto Notes export (Auto Notes window, Export). */
export const ImportOdAutoNotesRequestSchema = z.object({
  json: z.string().min(2).max(2_000_000),
  scope: z.enum(['user', 'org']).default('org'),
});
export const ImportResultSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.array(z.object({ name: z.string(), reason: z.string() })),
  templates: z.array(NoteTemplateSchema),
});
export type ImportResult = z.infer<typeof ImportResultSchema>;

/* ------------------------------------------------------------------------ */
/* Abbreviations (OD Quick Paste muscle memory)                              */
/* ------------------------------------------------------------------------ */

export const AbbreviationKeySchema = z.string().regex(/^;[a-z0-9]{1,15}$/);

export const AbbreviationSchema = z.object({
  id: IdSchema,
  abbr: AbbreviationKeySchema,
  expansion: z.string().min(1).max(2000),
  scope: z.enum(['user', 'org']),
});
export type Abbreviation = z.infer<typeof AbbreviationSchema>;

export const AbbreviationListSchema = z.object({ abbreviations: z.array(AbbreviationSchema) });
export const SaveAbbreviationRequestSchema = z.object({
  abbr: AbbreviationKeySchema,
  expansion: z.string().trim().min(1).max(2000),
  scope: z.enum(['user', 'org']).default('user'),
});

/* ------------------------------------------------------------------------ */
/* Note body                                                                 */
/* ------------------------------------------------------------------------ */

export const FieldValueSchema = z.union([
  z.string().max(4000),
  z.array(z.string().max(200)).max(40),
]);
export type FieldValue = z.infer<typeof FieldValueSchema>;

export const TextBlockSchema = z
  .object({ id: z.string().min(1).max(40), kind: z.literal('text'), text: z.string().max(40_000) })
  .strict();

export const TemplateBlockSchema = z
  .object({
    id: z.string().min(1).max(40),
    kind: z.literal('template'),
    templateKey: z.string().min(1),
    templateVersion: z.number().int().positive(),
    name: z.string(),
    /** Snapshot of the template at insert time: later template edits never change a note. */
    template: TemplateBodySchema,
    values: z.record(FieldKeySchema, FieldValueSchema.nullable()).default({}),
    /** Keys whose value came from a default or the scribe and is not yet confirmed. */
    pending: z.array(FieldKeySchema).default([]),
    /** Where each pending value came from. */
    origin: z.record(FieldKeySchema, z.enum(['default', 'ai'])).default({}),
  })
  .strict();
export type TemplateBlock = z.infer<typeof TemplateBlockSchema>;

export const NoteBlockSchema = z.discriminatedUnion('kind', [TextBlockSchema, TemplateBlockSchema]);
export type NoteBlock = z.infer<typeof NoteBlockSchema>;

export const NoteBodySchema = z
  .object({ v: z.literal(1), blocks: z.array(NoteBlockSchema).max(60) })
  .strict();
export type NoteBody = z.infer<typeof NoteBodySchema>;
export type NoteBodyInput = z.input<typeof NoteBodySchema>;

/** Why a note cannot be signed yet. */
export const SignBlockerSchema = z.object({
  blockId: z.string(),
  fieldKey: z.string().nullable(),
  label: z.string(),
  reason: z.enum(['required', 'unconfirmed_default', 'unconfirmed_ai', 'empty_note']),
});
export type SignBlocker = z.infer<typeof SignBlockerSchema>;

/* ------------------------------------------------------------------------ */
/* Notes                                                                     */
/* ------------------------------------------------------------------------ */

export const NOTE_STATUSES = [
  'draft',
  'pending_cosign',
  'signed',
  'locked_unsigned',
  'deleted',
  /** A note synced from the host PMS: the PMS's record, never signed or locked in Velora. */
  'pms',
] as const;
export const NoteStatusSchema = z.enum(NOTE_STATUSES);
export type NoteStatus = z.infer<typeof NoteStatusSchema>;

export const NoteKindSchema = z.enum(['note', 'addendum', 'amendment']);
export type NoteKind = z.infer<typeof NoteKindSchema>;

export const NoteSourceSchema = z.enum([
  'manual',
  'template',
  'scribe',
  'default_on_complete',
  'chart',
  'pms',
]);
export type NoteSource = z.infer<typeof NoteSourceSchema>;

export const PersonRefSchema = z.object({ id: IdSchema, name: z.string() });

/** A PMS-origin note's state in its PMS (status `pms`). */
export const PmsRecordSchema = z.object({
  pmsSystem: z.enum(['opendental', 'dentrix', 'eaglesoft']).nullable(),
  /** Signed in the PMS, as the PMS reports it; null when the PMS does not say. */
  signed: z.boolean().nullable(),
});
export type PmsRecord = z.infer<typeof PmsRecordSchema>;
export type PersonRef = z.infer<typeof PersonRefSchema>;

export const NoteSignatureSchema = z.object({
  role: z.enum(['primary', 'additional']),
  userId: IdSchema,
  name: z.string(),
  /** "DDS", "RDH", licence text from the provider profile. */
  credentials: z.string().nullable(),
  at: InstantSchema,
  /** Office time zone the stamp is shown in. */
  timeZone: z.string(),
  /** Signed after the note auto-locked. */
  late: z.boolean(),
});
export type NoteSignature = z.infer<typeof NoteSignatureSchema>;

export const WRITE_BACK_DETAIL = [
  'not_applicable',
  'blocked',
  'paused',
  'not_linked',
  'queued',
  'sent',
  'applied',
  'failed',
  'unknown',
  'conflict',
] as const;

export const NoteWriteBackSchema = z.object({
  /** Record-level dot state (WriteBackDot), or null when write-back does not apply. */
  state: WriteBackStateSchema.nullable(),
  detail: z.enum(WRITE_BACK_DETAIL),
  /** Human reason (blocked, paused, failed); never contains PHI. */
  message: z.string().nullable(),
  pmsSystem: z.enum(['opendental', 'dentrix', 'eaglesoft']).nullable(),
  commandId: IdSchema.nullable(),
  attempts: z.number().int().nonnegative(),
  updatedAt: InstantSchema.nullable(),
  /** A retry is allowed (failed or unknown, never money). */
  canRetry: z.boolean(),
  /**
   * Set when the text sent to the PMS had to be shortened (PMS limit 20,000
   * characters): the date line and signature are always kept, the body is cut.
   */
  warning: z.string().nullable().optional(),
});
export type NoteWriteBack = z.infer<typeof NoteWriteBackSchema>;

export const NoteAppendixSchema = z.object({
  id: IdSchema,
  kind: z.enum(['addendum', 'amendment']),
  text: z.string(),
  /** Amendment only: the reason, and the original passage struck through. */
  reason: z.string().nullable(),
  struckText: z.string().nullable(),
  author: PersonRefSchema,
  createdAt: InstantSchema,
  signature: NoteSignatureSchema.nullable(),
});
export type NoteAppendix = z.infer<typeof NoteAppendixSchema>;

export const ClinicalNoteSchema = z.object({
  id: IdSchema,
  patientId: IdSchema,
  locationId: IdSchema,
  appointmentId: IdSchema.nullable(),
  providerId: IdSchema.nullable(),
  provider: PersonRefSchema.nullable(),
  additionalProviderId: IdSchema.nullable(),
  additionalProvider: PersonRefSchema.nullable(),
  dateOfService: LocalDateSchema,
  timeZone: z.string(),
  status: NoteStatusSchema,
  source: NoteSourceSchema,
  teeth: z.array(z.string()),
  procedureIds: z.array(IdSchema),
  templates: z.array(z.object({ key: z.string(), version: z.number().int(), name: z.string() })),
  body: NoteBodySchema,
  renderedText: z.string(),
  draftedBy: PersonRefSchema.nullable(),
  signatures: z.array(NoteSignatureSchema),
  lockedAt: InstantSchema.nullable(),
  lockReason: z.enum(['signed', 'auto_lock', 'claim_submitted']).nullable(),
  /** When this unsigned draft auto-locks (office local midnight). */
  autoLocksAt: InstantSchema.nullable(),
  signatureRequested: z
    .object({ by: PersonRefSchema, at: InstantSchema, toUserId: IdSchema.nullable() })
    .nullable(),
  appendices: z.array(NoteAppendixSchema),
  blockers: z.array(SignBlockerSchema),
  writeBack: NoteWriteBackSchema,
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
  /** For a PMS-origin note: which PMS holds it and whether it is signed there. */
  pmsRecord: PmsRecordSchema.nullable().optional(),
  /** What the caller may do now (the UI never offers anything else). */
  actions: z.object({
    edit: z.boolean(),
    sign: z.boolean(),
    /** Present instead of sign when the caller is not the provider ("Send to Dr. A"). */
    requestSignature: z.boolean(),
    /**
     * "Sign as covering dentist": the caller is a dentist who may sign this
     * other dentist's unsigned note (dentist role test, finding 19). Signing
     * makes the caller the note's provider and is audited.
     */
    coverSign: z.boolean().optional(),
    addendum: z.boolean(),
    amend: z.boolean(),
    delete: z.boolean(),
  }),
  deletedReason: z.string().nullable(),
});
export type ClinicalNote = z.infer<typeof ClinicalNoteSchema>;

/**
 * Whether the office's own PMS clinical notes reach Velora (CH-04). PulseSync
 * v1 has no notes read, so at a PMS office Velora holds only notes written in
 * Velora; screens show `reason` instead of "No notes yet".
 */
export const PmsNotesFeedSchema = z.object({
  state: z.enum(['synced', 'not_synced', 'not_applicable']),
  pmsSystem: z.enum(['opendental', 'dentrix', 'eaglesoft']).nullable(),
  reason: z.string().nullable(),
});
export type PmsNotesFeed = z.infer<typeof PmsNotesFeedSchema>;

export const NoteListSchema = z.object({
  notes: z.array(ClinicalNoteSchema),
  total: z.number().int().nonnegative(),
  /** PMS notes feed for this patient's offices in scope (optional for older servers). */
  pmsNotes: PmsNotesFeedSchema.optional(),
});
export type NoteList = z.infer<typeof NoteListSchema>;

export const NoteListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(['all', 'unsigned', 'signed', 'deleted']).default('all'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

/* ------------------------------------------------------------------------ */
/* Composer context (CN-01: zero extra clicks)                               */
/* ------------------------------------------------------------------------ */

export const ContextProcedureSchema = z.object({
  id: IdSchema,
  code: z.string(),
  description: z.string(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  status: z.number().int().min(1).max(8),
  providerId: IdSchema.nullable(),
});
export type ContextProcedure = z.infer<typeof ContextProcedureSchema>;

export const ContextProviderSchema = z.object({
  id: IdSchema,
  name: z.string(),
  abbr: z.string(),
  type: z.enum(['dentist', 'hygienist', 'assistant', 'other']),
  userId: IdSchema.nullable(),
  credentials: z.string().nullable(),
});
export type ContextProvider = z.infer<typeof ContextProviderSchema>;

export const ContextAppointmentSchema = z.object({
  id: IdSchema,
  locationId: IdSchema,
  startsAt: InstantSchema,
  date: LocalDateSchema,
  typeName: z.string().nullable(),
  providerId: IdSchema.nullable(),
  additionalProviderId: IdSchema.nullable(),
  flowStatus: z.string(),
  completed: z.boolean(),
  procedures: z.array(ContextProcedureSchema),
  noteId: IdSchema.nullable(),
});
export type ContextAppointment = z.infer<typeof ContextAppointmentSchema>;

export const PatientHeaderSchema = z.object({
  id: IdSchema,
  name: z.string(),
  preferredName: z.string().nullable(),
  age: z.number().int().nullable(),
  chartNumber: z.string().nullable(),
  alerts: z.array(
    z.object({ kind: z.string(), description: z.string(), severity: z.string().nullable() }),
  ),
});
export type PatientHeader = z.infer<typeof PatientHeaderSchema>;

export const NoteContextQuerySchema = z.object({
  appointmentId: IdSchema.optional(),
});

export const NoteContextSchema = z.object({
  patient: PatientHeaderSchema,
  /** Today's appointment (or the requested one), when there is one. */
  appointment: ContextAppointmentSchema.nullable(),
  /** Recent visits the note can be attached to. */
  recentAppointments: z.array(ContextAppointmentSchema),
  providers: z.array(ContextProviderSchema),
  /** The caller's own provider row, when they chart as a provider. */
  me: z.object({ userId: IdSchema, name: z.string(), providerId: IdSchema.nullable() }),
  today: LocalDateSchema,
  timeZone: z.string(),
  offices: z.array(
    z.object({
      id: IdSchema,
      name: z.string(),
      timeZone: z.string(),
      writeBack: z.object({
        detail: z.enum(WRITE_BACK_DETAIL),
        message: z.string().nullable(),
        pmsSystem: z.enum(['opendental', 'dentrix', 'eaglesoft']).nullable(),
      }),
    }),
  ),
});
export type NoteContext = z.infer<typeof NoteContextSchema>;

/* ------------------------------------------------------------------------ */
/* Writes                                                                    */
/* ------------------------------------------------------------------------ */

export const CreateNoteRequestSchema = z.object({
  patientId: IdSchema,
  locationId: IdSchema,
  appointmentId: IdSchema.nullable().default(null),
  providerId: IdSchema,
  additionalProviderId: IdSchema.nullable().default(null),
  dateOfService: LocalDateSchema,
  teeth: z
    .array(z.string().regex(/^([1-9]|[12][0-9]|3[0-2]|[A-T])$/))
    .max(52)
    .default([]),
  procedureIds: z.array(IdSchema).max(100).default([]),
  body: NoteBodySchema,
  source: z.enum(['manual', 'template', 'default_on_complete']).default('manual'),
});
export type CreateNoteRequest = z.input<typeof CreateNoteRequestSchema>;

/** Autosave. `expectedVersion` makes concurrent edits a 409 with the server copy. */
export const UpdateNoteRequestSchema = z.object({
  expectedVersion: z.number().int().positive(),
  body: NoteBodySchema.optional(),
  providerId: IdSchema.optional(),
  additionalProviderId: IdSchema.nullable().optional(),
  appointmentId: IdSchema.nullable().optional(),
  dateOfService: LocalDateSchema.optional(),
  teeth: z
    .array(z.string().regex(/^([1-9]|[12][0-9]|3[0-2]|[A-T])$/))
    .max(52)
    .optional(),
  procedureIds: z.array(IdSchema).max(100).optional(),
});
export type UpdateNoteRequest = z.input<typeof UpdateNoteRequestSchema>;

/** 409 details when `expectedVersion` is stale: the merge view needs both copies. */
export const NoteConflictDetailsSchema = z.object({
  reason: z.literal('stale_version'),
  current: ClinicalNoteSchema,
  updatedByName: z.string().nullable(),
});
export type NoteConflictDetails = z.infer<typeof NoteConflictDetailsSchema>;

/** Step-up: the signer's own PIN or password, or an armed batch session. */
export const StepUpSchema = z.union([
  z.object({ pin: z.string().regex(/^\d{4,8}$/) }).strict(),
  z.object({ password: z.string().min(1).max(200) }).strict(),
  z.object({ armedToken: z.string().min(20).max(200) }).strict(),
]);
export type StepUp = z.infer<typeof StepUpSchema>;

export const SignNoteRequestSchema = z.object({
  expectedVersion: z.number().int().positive(),
  stepUp: StepUpSchema,
  /** Sign another dentist's note as the covering dentist (see `actions.coverSign`). */
  covering: z.boolean().optional(),
});
export type SignNoteRequest = z.input<typeof SignNoteRequestSchema>;

export const ArmSigningRequestSchema = z.object({
  stepUp: z.union([
    z.object({ pin: z.string().regex(/^\d{4,8}$/) }).strict(),
    z.object({ password: z.string().min(1).max(200) }).strict(),
  ]),
});
export const ArmedSessionSchema = z.object({
  token: z.string(),
  /** Hard stop: 15 minutes after arming. */
  expiresAt: InstantSchema,
  /** Idle stop: 5 minutes after the last signature. */
  idleExpiresAt: InstantSchema,
});
export type ArmedSession = z.infer<typeof ArmedSessionSchema>;

/** Which step-up factors the signer has (`GET /notes/sign-session`): no PIN means ask for the password. */
export const StepUpFactorsSchema = z.object({ hasPin: z.boolean() });
export type StepUpFactors = z.infer<typeof StepUpFactorsSchema>;

export const AddendumRequestSchema = z.object({
  text: z.string().trim().min(1).max(20_000),
  stepUp: StepUpSchema,
});
export const AmendmentRequestSchema = z.object({
  struckText: z.string().trim().min(1).max(20_000),
  replacement: z.string().trim().max(20_000).default(''),
  reason: z.string().trim().min(3).max(500),
  stepUp: StepUpSchema,
});
export const DeleteNoteRequestSchema = z.object({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});
export const RequestSignatureSchema = z.object({
  message: z.string().max(300).optional(),
});
export const SaveAsTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  category: TemplateCategorySchema,
});

/** Visit default: draft the templates bound to the visit's completed codes (CN-09). */
export const VisitDefaultsRequestSchema = z.object({ appointmentId: IdSchema });
export const VisitDefaultsResultSchema = z.object({
  created: z.array(ClinicalNoteSchema),
  existing: z.array(IdSchema),
});

/* ------------------------------------------------------------------------ */
/* Notes due (queue) and schedule indicators                                 */
/* ------------------------------------------------------------------------ */

export const QueueTabSchema = z.enum(['unsigned', 'missing', 'drafts', 'signed']);
export type QueueTab = z.infer<typeof QueueTabSchema>;

export const QueueQuerySchema = z.object({
  tab: QueueTabSchema.default('unsigned'),
  providerId: z.union([IdSchema, z.literal('me'), z.literal('all')]).default('all'),
  from: LocalDateSchema.optional(),
  to: LocalDateSchema.optional(),
});

export const QueueItemSchema = z.object({
  /** Note id, or null for a Missing row (a visit with no note). */
  noteId: IdSchema.nullable(),
  appointmentId: IdSchema.nullable(),
  patient: z.object({ id: IdSchema, name: z.string(), alert: z.string().nullable() }),
  locationId: IdSchema,
  dateOfService: LocalDateSchema,
  time: z.string().nullable(),
  visit: z.string(),
  provider: PersonRefSchema.nullable(),
  status: z.union([NoteStatusSchema, z.literal('missing')]),
  blockers: z.number().int().nonnegative(),
  /** First blocker reason, so batch signing can skip with a reason (CN-14). */
  blockerReason: z.string().nullable(),
  procedures: z.array(z.string()),
  signatureRequested: z.boolean(),
  /** The caller can sign this note now. */
  canSign: z.boolean(),
  writeBack: z.enum(WRITE_BACK_DETAIL),
  /** Status `pms`: the PMS and its signature state ("Signed in Dentrix"). */
  pmsRecord: PmsRecordSchema.nullable().optional(),
});
export type QueueItem = z.infer<typeof QueueItemSchema>;

export const QueueSchema = z.object({
  items: z.array(QueueItemSchema),
  counts: z.object({
    unsigned: z.number().int().nonnegative(),
    missing: z.number().int().nonnegative(),
    drafts: z.number().int().nonnegative(),
    signed: z.number().int().nonnegative(),
  }),
  from: LocalDateSchema,
  to: LocalDateSchema,
  providers: z.array(ContextProviderSchema),
  me: z.object({ userId: IdSchema, providerId: IdSchema.nullable() }),
  /**
   * Offices left out of the Missing tab and count (CH-20): their notes are
   * kept in the PMS and not synced, so a visit without a Velora note there is
   * not a missing note.
   */
  missingExcluded: z
    .array(
      z.object({
        locationId: IdSchema,
        name: z.string(),
        pmsSystem: z.enum(['opendental', 'dentrix', 'eaglesoft']),
      }),
    )
    .optional(),
});
export type Queue = z.infer<typeof QueueSchema>;

/** Per-appointment note state for schedule cards (none/draft/unsigned/signed). */
export const NOTE_INDICATORS = ['none', 'missing', 'draft', 'unsigned', 'signed'] as const;
export const NoteIndicatorSchema = z.enum(NOTE_INDICATORS);
export type NoteIndicator = z.infer<typeof NoteIndicatorSchema>;

export const ScheduleIndicatorsQuerySchema = z.object({ date: LocalDateSchema });
export const ScheduleIndicatorsSchema = z.object({
  date: LocalDateSchema,
  appointments: z.record(IdSchema, NoteIndicatorSchema),
  /** "3 notes due" per provider column. */
  dueByProvider: z.record(IdSchema, z.number().int().nonnegative()),
});
export type ScheduleIndicators = z.infer<typeof ScheduleIndicatorsSchema>;

/** Audit view: every saved version with a word-level diff against the previous one. */
export const NoteRevisionSchema = z.object({
  version: z.number().int().positive(),
  at: InstantSchema,
  actor: PersonRefSchema.nullable(),
  action: z.string(),
  text: z.string(),
});
export const NoteHistorySchema = z.object({ revisions: z.array(NoteRevisionSchema) });
export type NoteHistory = z.infer<typeof NoteHistorySchema>;

/** Live-update entity keys this module publishes. */
export const NOTES_ENTITY_KEYS = ['clinical_note', 'note_template'] as const;

/**
 * `GET /v1/notes/visit-prefill?patientId&locationId&date[&appointmentId]`
 * (notes.view, PHI-audited): values for a template's chips from today's
 * visit (perio exam, recall interval, medical-history review), pending until
 * confirmed (hygienist role test, finding 11).
 */
export const VisitPrefillQuerySchema = z.object({
  patientId: IdSchema,
  locationId: IdSchema,
  date: LocalDateSchema,
  appointmentId: IdSchema.optional(),
});
export const VisitPrefillSchema = z.object({
  values: z.record(z.string(), z.string()),
  perio: z
    .object({
      bopPercent: z.number().nullable(),
      worsenedSites: z.number().int(),
      furcationTeeth: z.number().int(),
      mobileTeeth: z.number().int(),
    })
    .nullable(),
});
export type VisitPrefill = z.infer<typeof VisitPrefillSchema>;

/* ------------------------------------------------------------------------ */
/* Today's note with AI (W8, Zac debrief 2026-10-28)                         */
/* ------------------------------------------------------------------------ */

/**
 * What the AI may use for today's note: the record facts for the visit,
 * read live. Each list is what Velora holds; nothing here is inferred.
 */
export const TodayNoteQuerySchema = z.object({
  patientId: IdSchema,
  locationId: IdSchema,
  date: LocalDateSchema.optional(),
  appointmentId: IdSchema.optional(),
});

export const TodayNoteProcSchema = z.object({
  code: z.string(),
  description: z.string().nullable(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
});
export type TodayNoteProc = z.infer<typeof TodayNoteProcSchema>;

export const TodayNoteFactsSchema = z.object({
  date: LocalDateSchema,
  locationId: IdSchema,
  appointment: z
    .object({
      id: IdSchema,
      time: z.string().nullable(),
      type: z.string().nullable(),
      provider: z.string().nullable(),
      /** The appointment's own note (reason for visit as booked). */
      note: z.string().nullable(),
    })
    .nullable(),
  /** Procedures completed at this office on the date. */
  completed: z.array(TodayNoteProcSchema),
  /** Planned procedures the visit was booked for (not yet completed). */
  scheduled: z.array(TodayNoteProcSchema),
  /** Conditions charted on the patient (open, not deleted). */
  conditions: z.array(TodayNoteProcSchema),
  /** Active medical alerts (allergies, premed, conditions). */
  alerts: z.array(z.string()),
  /** Today's perio exam against the one before; null with no exam today. */
  perio: z
    .object({
      bopPercent: z.number().nullable(),
      bopDelta: z.number().nullable(),
      sitesPdGte6: z.number().int(),
      worsened: z.array(z.string()),
      improvedSites: z.number().int(),
      furcation: z.array(z.string()),
      mobility: z.array(z.string()),
      compared: z.boolean(),
    })
    .nullable(),
  /** Medical history reviewed today: updated, no changes, or not reviewed. */
  medHx: z.enum(['updated', 'no changes']).nullable(),
  /** Whether an AI model is available for this practice right now (BAA lane). */
  ai: z.object({ available: z.boolean(), reason: z.string().nullable() }),
  /** Whether dictation can be transcribed (the on-prem speech lane). */
  dictation: z.object({ available: z.boolean() }),
});
export type TodayNoteFacts = z.infer<typeof TodayNoteFactsSchema>;

export const TodayNoteDraftRequestSchema = z.object({
  patientId: IdSchema,
  locationId: IdSchema,
  date: LocalDateSchema.optional(),
  appointmentId: IdSchema.optional(),
  /** The provider's dictation for this visit (transcribed or typed). */
  dictation: z.string().max(20_000).optional(),
  /** Extra context typed by the team ("pt nervous, wants to discuss whitening"). */
  context: z.string().max(4_000).optional(),
});
export type TodayNoteDraftRequest = z.infer<typeof TodayNoteDraftRequestSchema>;

export const TodayNoteDraftSchema = z.object({
  /** The suggested note text; a draft until the provider edits and signs it. */
  text: z.string(),
  /** Things in the draft that the facts do not support (check before signing). */
  warnings: z.array(z.string()),
  /** Bracketed blanks the provider must fill ("[anesthetic: type, carpules]"). */
  blanks: z.number().int().nonnegative(),
  model: z.string().nullable(),
  facts: TodayNoteFactsSchema,
});
export type TodayNoteDraft = z.infer<typeof TodayNoteDraftSchema>;

export const DictateRequestSchema = z.object({
  /** PCM16 little-endian mono, base64. At most about 30 s at 16 kHz per chunk. */
  pcm16Base64: z.string().min(4).max(1_400_000),
  sampleRateHz: z.number().int().min(8000).max(48000),
  locationId: IdSchema,
});
export const DictateResultSchema = z.object({ text: z.string() });
export type DictateResult = z.infer<typeof DictateResultSchema>;

/**
 * Notes for one day per provider (W8 item 3): who has unsigned notes and
 * which completed visits have no note at all. `mine` lists the caller's own.
 */
export const NotesDayQuerySchema = z.object({ date: LocalDateSchema.optional() });
export const NotesDayProviderSchema = z.object({
  providerId: IdSchema.nullable(),
  name: z.string(),
  unsigned: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
});
export const NotesDayItemSchema = z.object({
  kind: z.enum(['unsigned', 'missing']),
  noteId: IdSchema.nullable(),
  appointmentId: IdSchema.nullable(),
  patient: z.object({ id: IdSchema, name: z.string() }),
  locationId: IdSchema,
  time: z.string().nullable(),
  visit: z.string(),
  procedures: z.array(z.string()),
});
export type NotesDayItem = z.infer<typeof NotesDayItemSchema>;
export const NotesDaySchema = z.object({
  date: LocalDateSchema,
  me: z.object({ providerId: IdSchema.nullable() }),
  providers: z.array(NotesDayProviderSchema),
  mine: z.array(NotesDayItemSchema),
  /** Offices whose notes stay in the PMS (not synced): no "missing" there. */
  missingExcluded: z.array(z.string()),
});
export type NotesDay = z.infer<typeof NotesDaySchema>;
